import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { Badge, act, cleanLabel, fmtStamp } from "./shared";

const sel = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";
const ord = (n) => `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;

function History({ items }) {
  if (!items?.length) return <p className="text-xs text-slate-400">No corrections yet.</p>;
  return (
    <ol className="space-y-2" data-testid="raw-history">
      {[...items].reverse().map((h, i) => (
        <li key={i} className="rounded-md bg-slate-50 px-3 py-2 text-xs">
          <div className="font-semibold text-slate-700">{h.action === "edit" ? "Corrected" : h.action === "voided" ? "Voided" : "Restored"} · {h.by} · {fmtStamp(h.at)}</div>
          {Object.entries(h.changes || {}).map(([k, [a, b]]) => (
            <div key={k} className="text-slate-500">{k}: <span className="line-through">{Array.isArray(a) ? a.filter(Boolean).map(cleanLabel).join(", ") : String(a ?? "—")}</span> → <span className="text-slate-800">{Array.isArray(b) ? b.filter(Boolean).map(cleanLabel).join(", ") : String(b ?? "—")}</span></div>
          ))}
          {h.note && <div className="text-slate-500 italic">“{h.note}”</div>}
        </li>
      ))}
    </ol>
  );
}

export default function SubmissionDialog({ id, canEdit, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [opts, setOpts] = useState({ employees: [], jobs: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/timesheet/raw/submissions/${id}`).then(({ data }) => {
      setD(data);
      setF({ employee: data.employee, work_date: data.work_date || "", location_count: data.location_count ?? 0, job_labels: [...(data.job_labels || []), "", "", "", ""].slice(0, Math.max(4, (data.job_labels || []).length, data.location_count || 0)), note: "" });
    });
    if (canEdit) {
      Promise.all([api.get("/timesheet/employees"), api.get("/timesheet/jobs")]).then(([e, j]) =>
        setOpts({ employees: e.data.items.map((x) => x.name), jobs: j.data.items.map((x) => x.label) })).catch(() => {});
    }
  }, [id, canEdit]);

  const save = async () => {
    setBusy(true);
    const r = await act(() => api.put(`/timesheet/raw/submissions/${id}`, { ...f, location_count: Number(f.location_count) }), "Correction saved — dashboard updated");
    setBusy(false);
    if (r) { onSaved(); onClose(); }
  };
  const setStatus = async (kind) => {
    const reason = window.prompt(kind === "void" ? "Why are you voiding this submission?" : "Note for restoring (optional)") ?? null;
    if (reason === null) return;
    if (await act(() => api.post(`/timesheet/raw/submissions/${id}/${kind}`, { reason }), kind === "void" ? "Submission voided" : "Submission restored")) { onSaved(); onClose(); }
  };
  const withCur = (list, cur) => (cur && !list.includes(cur) ? [cur, ...list] : list);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="raw-submission-dialog">
        <DialogHeader>
          <DialogTitle>Timesheet submission</DialogTitle>
          <DialogDescription>
            {d ? <>{d.source === "strata" ? "Strata form" : d.source === "manual" ? `Manual entry by ${d.created_by || "staff"}` : "Typeform / Google Sheet"}{d.note ? ` · “${d.note}”` : ""}{d.via ? ` · ${d.via.replace("_", " ")}` : ""} · submitted {d.submitted_at_display || "—"}{d.legacy_token ? ` · token ${d.legacy_token}` : ""}</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {d && f && (
          <div className="space-y-4">
            {d.status !== "active" && <Badge tone="red">{d.status === "voided" ? `Voided${d.void_reason ? ` — ${d.void_reason}` : ""}` : "Removed from the Google Sheet"}</Badge>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Employee</Label>
                {canEdit ? <select className={sel} disabled={!opts.employees.length} value={f.employee} onChange={(e) => setF({ ...f, employee: e.target.value })} data-testid="raw-edit-employee">{withCur(opts.employees, f.employee).map((n) => <option key={n}>{n}</option>)}</select> : <div className="text-sm">{d.employee}</div>}
              </div>
              <div className="space-y-1"><Label className="text-xs">Date worked</Label>
                {canEdit ? <Input type="date" className="cal-white h-9" value={f.work_date} onChange={(e) => setF({ ...f, work_date: e.target.value })} data-testid="raw-edit-date" /> : <div className="text-sm">{d.work_date}</div>}
              </div>
              <div className="space-y-1"><Label className="text-xs">Locations</Label>
                {canEdit ? <select className={sel} value={f.location_count} onChange={(e) => { const n = Number(e.target.value); setF({ ...f, location_count: n, job_labels: [...f.job_labels, ...Array(Math.max(0, n - f.job_labels.length)).fill("")] }); }} data-testid="raw-edit-count">{Array.from({ length: 21 }, (_, n) => <option key={n}>{n}</option>)}</select> : <div className="text-sm">{d.location_count}</div>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {f.job_labels.map((j, i) => (
                <div key={i} className="space-y-1"><Label className="text-xs">{ord(i + 1)} jobsite</Label>
                  {canEdit ? (
                    <select className={sel} disabled={!opts.jobs.length} value={j} onChange={(e) => { const n = [...f.job_labels]; n[i] = e.target.value; setF({ ...f, job_labels: n }); }} data-testid={`raw-edit-job-${i + 1}`}>
                      <option value="">—</option>{withCur(opts.jobs, j).map((x) => <option key={x} value={x}>{cleanLabel(x)}</option>)}
                    </select>
                  ) : <div className="text-sm">{cleanLabel(j) || "—"}</div>}
                </div>
              ))}
            </div>
            {Object.keys(d.answers || {}).length > 0 && (
              <div className="space-y-1"><Label className="text-xs">Other answers</Label>
                {Object.entries(d.answers).map(([k, v]) => <div key={k} className="text-sm"><span className="text-slate-500">{k}:</span> {v}</div>)}
              </div>
            )}
            {canEdit && <div className="space-y-1"><Label className="text-xs">Correction note</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Why is this being changed?" data-testid="raw-edit-note" /></div>}
            {canEdit && <div className="space-y-2"><Label className="text-xs">Edit history</Label><History items={d.history} /></div>}
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          {canEdit && d ? (d.status === "voided"
            ? <Button variant="outline" onClick={() => setStatus("restore")} data-testid="raw-restore-btn">Restore</Button>
            : <Button variant="outline" className="text-rose-600" onClick={() => setStatus("void")} disabled={d.status !== "active"} data-testid="raw-void-btn">Void</Button>) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="raw-dialog-close">Close</Button>
            {canEdit && <Button onClick={save} disabled={busy || !f || !opts.jobs.length} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="raw-save-btn">{busy ? "Saving…" : "Save correction"}</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
